import { Input } from '@/components/ui/input';

import { VariableAutocomplete } from '../VariableAutocomplete';

import type { TrackingLinkNodeData } from '@repo/shared-types';

interface TrackingLinkConfigProps {
  data: TrackingLinkNodeData;
  onChange: (data: TrackingLinkNodeData) => void;
}

const LINK_TYPES: TrackingLinkNodeData['linkType'][] = [
  'WORKFLOW_VEHICLE_LINK',
  'VEHICLE_LINK',
  'WORKFLOW',
  'RESERVATION_LINK',
  'PART_EXCHANGE_LINK',
  'PAYMENT_LINK',
  'FINANCE_LINK',
];

export const TrackingLinkConfig = ({ data, onChange }: TrackingLinkConfigProps) => {
  const update = (fields: Partial<TrackingLinkNodeData>) => {
    onChange({ ...data, ...fields });
  };

  const outputKey = data.outputKey || 'tracking';

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="tl-label">
          Label
        </label>
        <Input
          id="tl-label"
          value={data.label || ''}
          onChange={(e) => update({ label: e.target.value })}
          placeholder="Tracking Link"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="tl-url">
          Destination URL
        </label>
        <VariableAutocomplete
          value={data.url || ''}
          onChange={(v) => update({ url: v })}
          placeholder="{{contact.data.vehicle_url}}"
        />
        <p className="mt-1 text-[10px] text-gray-500">
          Wrapped in a /r/ redirect so the click and its timestamp are recorded.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="tl-link-type">
          Link Type
        </label>
        <select
          id="tl-link-type"
          className="w-full rounded-md border border-gray-700 bg-transparent px-3 py-2 text-sm"
          value={data.linkType || 'WORKFLOW_VEHICLE_LINK'}
          onChange={(e) => update({ linkType: e.target.value as TrackingLinkNodeData['linkType'] })}
        >
          {LINK_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="tl-call-id">
          Call ID <span className="text-[10px] font-normal">(optional)</span>
        </label>
        <VariableAutocomplete
          value={data.callId || ''}
          onChange={(v) => update({ callId: v || undefined })}
          placeholder="{{webhook.body.callId}}"
        />
        <p className="mt-1 text-[10px] text-gray-500">
          Attributes the click to a call. Leave blank on webhook and scheduled runs — the link still
          works and is tracked without one.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="tl-output-key">
          Output Key
        </label>
        <Input
          id="tl-output-key"
          value={data.outputKey || ''}
          onChange={(e) => update({ outputKey: e.target.value })}
          placeholder="tracking"
        />
        <p className="mt-1 text-[10px] text-gray-500">
          Available downstream as <code className="text-gray-400">{`{{${outputKey}.url}}`}</code>{' '}
          and <code className="text-gray-400">{`{{${outputKey}.id}}`}</code> (the id alone suits a
          WhatsApp URL-button suffix).
        </p>
      </div>
    </div>
  );
};
